import styled from '@emotion/styled';
import {VersionInfo} from 'electron-communication-lib/BrowserForApp';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ESmallButton} from 'webapp-lib/Components';
import {mixinSVGIcon} from 'webapp-lib/styling';

import {Warning} from '_/assets/images';
import {useVersionInfo} from '_/hooks/useVersionInfo';

export function VersionUpdateInfo({
  browserUpdateInfo,
  onUpdate,
}: {
  browserUpdateInfo: VersionInfo | undefined;
  onUpdate: () => void;
}): React.ReactElement | null {
  const {t} = useTranslation('common');

  if (!browserUpdateInfo) {
    return null;
  }

  const [semverCore] = browserUpdateInfo.version.split('-');

  return (
    <WarningStyle>
      <Warning />
      {t('topBar.versionWarning.newBrowserVersionAvailable', {
        version: semverCore,
      })}
      {browserUpdateInfo.updateDownloaded ? (
        <ESmallButton onClick={onUpdate}>
          {t('topBar.versionWarning.update')}
        </ESmallButton>
      ) : null}
    </WarningStyle>
  );
}

export function VersionMismatchWarning(): React.ReactElement | null {
  const {t} = useTranslation('common');
  const {frontend, backend} = useVersionInfo();
  const [isLoading, setIsLoading] = useState(true);
  const [isMatch, setIsMatch] = useState<boolean>(false);

  useEffect(() => {
    if (frontend === undefined || backend === undefined) {
      return;
    }
    setIsLoading(false);

    if (frontend.includes('local') && backend.includes('local')) {
      setIsMatch(true);
      return;
    }

    setIsMatch(versionsEq(frontend, backend));
  }, [frontend, backend]);

  if (isLoading || isMatch) {
    return null;
  }

  return (
    <WarningStyle>
      <Warning />
      {t('topBar.versionWarning.frontendBackendVersionMismatch')}
    </WarningStyle>
  );
}

const VERSION_REGEX = /(.+)-g(\p{Hex_Digit}+)-build(\d+)/u;
const versionsEq = (a: string, b: string): boolean => {
  const [, versionA, commitA] = a.match(VERSION_REGEX) ?? [undefined];
  const [, versionB, commitB] = b.match(VERSION_REGEX) ?? [undefined];
  if (!versionA || !versionB || !commitA || !commitB) {
    return false;
  }
  const trimLength = Math.min(commitA.length, commitB.length);
  return (
    versionA === versionB &&
    commitA.slice(0, trimLength) === commitB.slice(0, trimLength)
  );
};

const WarningStyle = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  gap: var(--gutter-little);
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-caution);
  color: var(--color-core-white);
  padding: var(--gutter-little);

  svg {
    ${mixinSVGIcon};
  }
`;
